import { useEffect } from "react";
import Box from "@mui/material/Box";
import AnimButton from "../mui/AniButton";
import Step from "@mui/material/Step";
import Button from "@mui/material/Button";
import LoaderButton from "../mui/LoaderButton";
import { Done } from "@mui/icons-material";
import Stepper from "@mui/material/Stepper";
import { useNavigate } from "react-router-dom";
import StepLabel from "@mui/material/StepLabel";
import Typography from "@mui/material/Typography";
import StepContent from "@mui/material/StepContent";
import useManualSwap from "./hooks/use-manual-swap";

function CustomStepIcon({ active, completed, index }) {
  return (
    <Box
      sx={{
        width: active ? 40 : 30,
        height: active ? 40 : 30,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: active || completed ? "#1DA7A7" : "grey.400",
        color: "#FFFFFF",
        borderRadius: "50%",
        ml: active ? "0px" : "5px",
      }}
    >
      {completed ? (
        <Done />
      ) : (
        <Typography variant={active ? "h6" : "body2"} sx={{ fontWeight: 600 }}>
          {index + 1}
        </Typography>
      )}
    </Box>
  );
}

export default function SwapVerticalLinearStepper({
  renderStepContent,
  useStepperContext,
  onStepCompletion,
  swapType,
}) {
  const {
    data,
    activeStep,
    updateData,
    handleNext,
    handleBack,
    handleReset,
    steps,
    setSteps,
  } = useStepperContext();

  const navigate = useNavigate();
  const { isLoading, onSubmitManualSwap } = useManualSwap();

  const handleStepCompletion = () => {
    if (onStepCompletion?.[activeStep]) {
      onStepCompletion[activeStep](data, steps, setSteps);
    }
    handleNext();
  };

  const handleSubmit = async () => {
    const payload = {
      deviceId: data.deviceId,
      partnerId: data.partnerId,
      driverId: data.driverId,
      manualSwapType: swapType,
      reasonForSwap: data.customReason || data.swapReason,
      insertedBatteryId: data.insertedBatteryId,
      takenBatteryId: data.takenBatteryId,
      takenBatteryVoltage: data.takenBatteryVoltage,
      insertedBatteryVoltage: data.insertedBatteryVoltage,
    };

    try {
      await onSubmitManualSwap(payload);
      handleStepCompletion();
    } catch (error) {
      console.log("error: ", error);
    }
  };

  // Returns true when the step's primary button should be disabled
  const isNextDisabled = (index) => {
    switch (index) {
      case 0:
        return data.swapReason !== "Other"
          ? !data.swapReason
          : !data.customReason;
      case 1:
        return (
          !data.partnerId ||
          !data.driverId ||
          !data.deviceId ||
          !data.isDriverDetails
        );
      case 2:
        return !data.insertedBatteryId || !data.insertedBatteryVoltage;
      case 3:
        return !data.takenBatteryId || !data.takenBatteryVoltage;
      default:
        return false;
    }
  };

  useEffect(() => {
    updateData("swapType", swapType);
  }, [swapType]);

  return (
    <Stepper
      activeStep={activeStep}
      orientation="vertical"
      sx={{
        "& .MuiStepConnector-lineVertical": {
          ml: "8px",
        },
      }}
    >
      {steps?.map((step, index) => (
        <Step
          key={step.label}
          expanded={step?.expanded && activeStep >= index}
          completed={step.completed}
        >
          <StepLabel
            StepIconComponent={(props) => (
              <CustomStepIcon
                {...props}
                index={index}
                active={index === activeStep}
              />
            )}
            optional={
              index === steps.length - 1 ? (
                <Typography variant="caption">Last step</Typography>
              ) : null
            }
          >
            {step.label}
          </StepLabel>
          <StepContent
            sx={{ ml: "20px" }}
            TransitionProps={{ unmountOnExit: true }}
          >
            {renderStepContent(
              step,
              index,
              data,
              updateData,
              swapType,
              handleSubmit,
            )}
            {activeStep === index && (
              <Box sx={{ mt: 2 }}>
                {index !== steps.length - 1 && (
                  <Button
                    variant="outlined"
                    onClick={
                      index === 0 ? () => navigate("/manual-swap") : handleBack
                    }
                    sx={{ mr: 2 }}
                  >
                    Back
                  </Button>
                )}

                {index === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleReset}
                    sx={{ my: 1 }}
                  >
                    Swap Again
                  </Button>
                ) : index === steps.length - 2 ? (
                  <LoaderButton
                    variant="contained"
                    onClick={handleSubmit}
                    isLoading={isLoading}
                    disabled={
                      data.takenBatteryId === data.insertedBatteryId ||
                      isNextDisabled(index)
                    }
                    sx={{ width: "auto", minWidth: "150px" }}
                  >
                    Submit
                  </LoaderButton>
                ) : (
                  <AnimButton
                    onClick={handleStepCompletion}
                    disabled={isNextDisabled(index)}
                    index={index}
                  >
                    Next
                  </AnimButton>
                )}
              </Box>
            )}
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
}